import React from 'react'
import { Menu, MenuItem } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'

import { faTh } from '@fortawesome/pro-duotone-svg-icons/faTh'
import { faLink } from '@fortawesome/pro-duotone-svg-icons/faLink'
import { faShareAlt } from '@fortawesome/pro-duotone-svg-icons/faShareAlt'
import { faLock } from '@fortawesome/pro-duotone-svg-icons/faLock'
import { faAngleDown } from '@fortawesome/pro-duotone-svg-icons'

import ContentDossierArchive from './ContentDossierArchive'
import ContentDossierDelete from './ContentDossierDelete'
import ContentDossierFavorite from './ContentDossierFavorite'
import ContentDossierHistory from './ContentDossierHistory'
import ContentDossierNotifications from './ContentDossierNotifications'
import ContentDossierSend from './ContentDossierSend'
import ContentDossierExport from '@/feature/DefDossier/DefDossierToolbar/ContentDossierExport'
import ContentDossierSecureShare from './ContentDossierSecureShare'

import {
    ContentDossierToolbarProps,
    ContentDossierToolbarState,
    ToastConfig,
} from '../types'

import { ToolbarButton } from '@/Components/Elements/StyledComponents/Buttons'

class ContentDossierToolbar extends React.Component<
    ContentDossierToolbarProps,
    ContentDossierToolbarState
> {
    toastOptions: ToastConfig

    constructor(props: ContentDossierToolbarProps) {
        super(props)

        this.state = {
            id: props.id,
            showSecureModal: false,
            showExportModal: false,
            anchorEl: null as null | HTMLElement,
            actionsAnchorEl: null,
        }

        this.toastOptions = {
            position: 'bottom-center',
            autoClose: 2500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        }

        this.handleCopyLink = this.handleCopyLink.bind(this)
    }

    isAllowed(credential: string): boolean {
        if (
            this.props.kedo.user().isSuperAdmin() ||
            this.props.kedo.env().isEnvironmentAdmin()
        ) {
            return true
        }

        return this.props.kedo
            .env()
            .isAllowedDefDossier(credential, this.props.dossier.def_dossier_id)
    }

    handleCopyLink(): void {
        let url = ''

        if (this.props.embedded) {
            url = `${window.location.host}/contentdossier/${this.props.id}?selectedTab=${this.props.activeTabKey}`
        } else {
            url = window.location.href
        }

        navigator.clipboard.writeText(url)
        toast.success(this.props.kedo.t('Link Copied'), this.toastOptions)
    }

    handleShareOpen = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({ anchorEl: event.currentTarget })
    }

    handleShareClose = () => {
        this.setState({ anchorEl: null })
    }

    handleActionsOpen = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({ actionsAnchorEl: event.currentTarget })
    }

    handleActionsClose = () => {
        this.setState({ actionsAnchorEl: null })
    }

    renderSecureShareModal() {
        return (
            <ContentDossierSecureShare
                contentDossier={this.props.dossier}
                kedo={this.props.kedo}
                closeModal={() => this.setState({ showSecureModal: false })}
            />
        )
    }

    isSecureShareEnabled(dossier: any): boolean {
        const secureShare = this.props.kedo.env().getModule('secureshare')

        if (
            !secureShare ||
            !secureShare.settings ||
            !Array.isArray(secureShare.settings.entities)
        ) {
            return false
        }

        return (
            secureShare.settings.entities.filter(
                (item: any) =>
                    parseInt(item.def_dossier_id, 10) ===
                    parseInt(dossier.def_dossier_id, 10)
            ).length > 0
        )
    }

    render() {
        const { kedo, modalized } = this.props
        const { anchorEl } = this.state
        const open = Boolean(anchorEl)
        let shareEnabled = false
        if (
            kedo.env().hasModule('secureshare') &&
            this.isSecureShareEnabled(this.props.dossier)
        ) {
            shareEnabled = true
        }

        return (
            <div
                className={
                    this.props.size ? 'contentDossierButtons' : undefined
                }
            >
                {this.state.showSecureModal
                    ? this.renderSecureShareModal()
                    : null}

                {(kedo.env().hasModule('secureshare') &&
                    !kedo.isAdminOrEnvironmentAdmin()) ||
                kedo
                    .env()
                    .isAllowedDefDossier(
                        'secureshare',
                        this.props.dossier.def_dossier_id,
                        kedo.user()
                    ) ? (
                    <>
                        {kedo.hasPermissionFor('kedo.defdossier.delete') ? (
                            <ContentDossierDelete
                                onSuccess={() => {
                                    this.handleActionsClose()
                                    this.props.onDeleteSuccess()
                                }}
                                kedo={kedo}
                                dossier={this.props.dossier}
                                size={modalized ? 'sm' : undefined}
                            />
                        ) : null}
                        {this.props.defdossier.archivable &&
                        this.isAllowed('archive') ? (
                            <ContentDossierArchive
                                size={modalized ? 'sm' : undefined}
                                kedo={kedo}
                                dossier={this.props.dossier}
                            />
                        ) : null}
                        {kedo.hasPermissionFor('kedo.defdossier.update') ? (
                            <ToolbarButton
                                component={Link}
                                to={`/fieldeditor/${this.props.dossier.def_dossier_id}`}
                                dense={modalized}
                                variant="outlined"
                                title={kedo.t('Field designer')}
                            >
                                <FontAwesomeIcon icon={faTh} />
                            </ToolbarButton>
                        ) : null}
                        <ToolbarButton
                            dense={modalized}
                            onClick={this.handleShareOpen}
                            variant="outlined"
                            title={kedo.t('Copy link')}
                            id="dropdown-share"
                            endIcon={
                                <FontAwesomeIcon icon={faAngleDown} size="sm" />
                            }
                            sx={{
                                '& .MuiButton-endIcon > svg ': {
                                    fontSize: modalized
                                        ? '0.875rem'
                                        : 'undefined',
                                },
                            }}
                        >
                            <FontAwesomeIcon icon={faLink} />
                        </ToolbarButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={this.handleShareClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            <MenuItem
                                dense
                                onClick={() => {
                                    this.handleCopyLink()
                                    this.handleShareClose()
                                }}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                {kedo.t('Copy link')}
                                <FontAwesomeIcon icon={faShareAlt} />
                            </MenuItem>

                            <MenuItem
                                dense
                                disabled={!shareEnabled}
                                onClick={() => {
                                    this.setState({
                                        showSecureModal:
                                            !this.state.showSecureModal,
                                    })
                                    this.handleShareClose()
                                }}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                {kedo.t('Secure share', { ns: 'secureshare' })}
                                <FontAwesomeIcon icon={faLock} />
                            </MenuItem>
                        </Menu>
                    </>
                ) : (
                    <ToolbarButton
                        dense={modalized}
                        onClick={this.handleCopyLink}
                        variant="outlined"
                        title={kedo.t('Copy link')}
                        sx={{
                            height: !modalized ? '42px' : undefined,
                        }}
                    >
                        <FontAwesomeIcon icon={faLink} />
                    </ToolbarButton>
                )}

                <ContentDossierSend
                    size={modalized ? 'sm' : undefined}
                    kedo={kedo}
                    dossier={this.props.dossier}
                />

                <ContentDossierFavorite
                    size={modalized ? 'sm' : undefined}
                    favorite={this.props.favorite}
                    kedo={kedo}
                    dossier={this.props.dossier}
                />

                <ContentDossierNotifications
                    size={modalized ? 'sm' : undefined}
                    subscribed={this.props.subscribed}
                    kedo={kedo}
                    dossier={this.props.dossier}
                />

                {this.isAllowed('export') ||
                this.isAllowed('export_csv') ||
                this.isAllowed('export_pdf') ||
                this.isAllowed('export_word') ||
                this.isAllowed('export_hml') ? (
                    <ContentDossierExport
                        size={modalized ? 'sm' : null}
                        defDossier={this.props.defdossier}
                        defDossierId={this.props.dossier.def_dossier_id}
                        kedo={kedo}
                        onClose={() =>
                            this.setState({ showExportModal: false })
                        }
                        selected={[this.props.id]}
                        mode="dossier"
                    />
                ) : null}

                {this.isAllowed('dossierlog') ? (
                    <ContentDossierHistory
                        size={modalized ? 'sm' : undefined}
                        kedo={kedo}
                        dossier={this.props.dossier}
                    />
                ) : null}
            </div>
        )
    }
}

export default ContentDossierToolbar
